import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { ToggleGroup, ToggleItem } from '../ui/toggle';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { gameDepartmentAPI } from '../../services/api';
import { SortableHead, TablePagination } from './TableControls';
import ReleaseTypeSummary from './ReleaseTypeSummary';
import GameTitleButton from './GameTitleButton';
import useBrandChange from './useBrandChange';
import {
  MAX_COMPARE_GAMES, compareNumeric, rangeForLastDays, toRequestRange, todayUtc,
} from './performanceData';
import {
  LIST_SCOPES, PHASES, PRE_RELEASE_WINDOW, WINDOWS, asPhase, comparisonRange, filterByScope, filterReleases, isPreReleaseType, isoDay, neverPlayed, periodLabel, releaseTypeMeta, summarizeByType, windowLabel,
} from './releaseData';

const TABLE_PAGE_SIZE = 50;

const STRING_COLS = new Set(['game_title', 'provider', 'release_type', 'release_date']);

// Release-type filter presets. A VIP phase is always part of a pre-release.
const TYPE_FILTERS = [
  { key: 'all',             label: 'All releases',                  types: ['pre_release_vip', 'pre_release', 'standard', 'unknown'] },
  { key: 'pre_any',         label: 'All pre-releases',              types: ['pre_release_vip', 'pre_release'] },
  { key: 'pre_release_vip', label: 'Pre-releases with a VIP phase', types: ['pre_release_vip'] },
  { key: 'pre_release',     label: 'Pre-releases without VIP',      types: ['pre_release'] },
  { key: 'standard',        label: 'Standard releases',             types: ['standard'] },
];

const DATE_RANGE_PRESETS = [
  { label: '90d', days: 90 },
  { label: '6m',  days: 182 },
  { label: '1y',  days: 365 },
];

function fmtInt(v) { return Number(v).toLocaleString('en-GB'); }
function fmtEur(v) { return `€${Number(v).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`; }
function fmtPct(v) { return v == null ? '—' : `${Number(v).toFixed(2)}%`; }
function fmtDay(v) {
  const day = isoDay(v);
  return day ? new Date(`${day}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '—';
}

const METRIC_COLS = [
  { key: 'players',      label: 'Players',  fmt: fmtInt },
  { key: 'turnover_eur', label: 'Turnover', fmt: fmtEur },
  { key: 'ggr_eur',      label: 'GGR',      fmt: fmtEur },
  { key: 'payout_pct',   label: 'Payout',   fmt: fmtPct },
];

function toPerformanceGame(g) {
  return { unique_game_id: g.unique_game_id, game_title: g.game_title, provider: g.provider, release_date: g.release_date };
}

// The measured period's dates and length; '—' when the game has no window
// (not pre-released, while measuring pre-release periods).
function PeriodCell({ g, prePeriod }) {
  if (g.window_start == null) return <TableCell className="text-ink-mute">—</TableCell>;
  // window_end is exclusive: the period's last day is the day before.
  const lastDay = new Date(g.window_end);
  lastDay.setUTCDate(lastDay.getUTCDate() - 1);
  const running = g.days_observed < g.window_length;
  return (
    <TableCell className="whitespace-nowrap">
      <span className="text-ink-dim">{windowLabel(g.window_start, lastDay.toISOString())}</span>
      <span className="text-ink-mute">
        {' · '}{g.window_length}d{prePeriod && !isPreReleaseType(g.release_type) ? ' after release (no pre-release)' : ''}
      </span>
      {running && (
        <p className="text-[11px] text-warning">Day {g.days_observed} of {g.window_length} — not in summary</p>
      )}
      {neverPlayed(g) && (
        <p className="text-[11px] text-warning">No players — probably never live, not in summary</p>
      )}
    </TableCell>
  );
}

function ReleaseRow({ g, prePeriod, selected, disabled, onToggle, onOpen }) {
  const type = releaseTypeMeta(g.release_type);
  const measured = g.window_start != null;
  return (
    <TableRow onClick={() => onOpen(g)} className="cursor-pointer">
      {/* Ticking must not also trigger the row's open-in-performance click. */}
      <TableCell className="w-8" onClick={e => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          disabled={disabled}
          onChange={() => onToggle(g)}
          aria-label={`Compare ${g.game_title}`}
          className="accent-cta h-4 w-4 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
        />
      </TableCell>
      <TableCell className="min-w-[160px]">
        <GameTitleButton title={g.game_title} onActivate={() => onOpen(g)} label={`Open ${g.game_title} in Game Performance`} />
      </TableCell>
      <TableCell className="text-ink-dim">{g.provider}</TableCell>
      <TableCell><Badge variant={type.variant} className="whitespace-nowrap">{type.label}</Badge></TableCell>
      <TableCell className="whitespace-nowrap">{fmtDay(g.release_date)}</TableCell>
      <TableCell className="whitespace-nowrap text-ink-dim">{fmtDay(g.planned_release_date_ss)}</TableCell>
      <PeriodCell g={g} prePeriod={prePeriod} />
      <TableCell className="whitespace-nowrap text-ink-dim">
        {/* vip_only_end is exclusive, like every period end: show the last VIP day. */}
        {g.vip_only_start && g.vip_only_end
          ? periodLabel(new Date(g.vip_only_start), new Date(g.vip_only_end))
          : windowLabel(g.vip_only_start, null) || '—'}
      </TableCell>
      {METRIC_COLS.map(m => (
        <TableCell key={m.key} className="text-right">{measured ? m.fmt(g[m.key]) : '—'}</TableCell>
      ))}
    </TableRow>
  );
}

export default function GameReleasesReport() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [range, setRange] = useState(() => rangeForLastDays(182));
  const [windowKey, setWindowKey] = useState(PRE_RELEASE_WINDOW);
  const [phase, setPhase] = useState('combined');
  const [listScope, setListScope] = useState('in_summary');
  const [typeFilter, setTypeFilter] = useState('pre_any');
  const [provider, setProvider] = useState('all');
  const [sortCol, setSortCol] = useState('release_date');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [compareSelection, setCompareSelection] = useState([]);

  const activeBrand = useBrandChange(() => {
    setProvider('all');
    setRows([]);
    setCompareSelection([]);
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    gameDepartmentAPI.getGameReleases({ ...toRequestRange(range), windowDays: windowKey })
      .then(res => { if (!cancelled) setRows(res.data?.games || []); })
      .catch(() => { if (!cancelled) setError('Failed to load game releases.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range, windowKey, activeBrand]);

  const providers = useMemo(() => (
    Array.from(new Set(rows.map(g => g.provider).filter(Boolean))).sort()
  ), [rows]);

  const prePeriod = windowKey === PRE_RELEASE_WINDOW;
  const activePhase = prePeriod ? phase : 'combined';

  // The phase tabs re-measure every pre-release over that phase of its
  // pre-release period (VIP-only week, or the open part after it).
  const filtered = useMemo(() => {
    const types = new Set(TYPE_FILTERS.find(f => f.key === typeFilter).types);
    const matching = filterReleases(rows, { types, provider });
    return matching.map(r => asPhase(r, activePhase));
  }, [rows, typeFilter, provider, activePhase]);

  const summary = useMemo(() => summarizeByType(filtered), [filtered]);

  const scopeCounts = useMemo(() => Object.fromEntries(
    LIST_SCOPES.map(s => [s.key, filterByScope(filtered, s.key).length]),
  ), [filtered]);

  const tableRows = useMemo(() => (
    filterByScope(filtered, listScope).sort((a, b) => {
      if (!STRING_COLS.has(sortCol)) return compareNumeric(a[sortCol], b[sortCol], sortDir);
      const cmp = String(a[sortCol]).localeCompare(String(b[sortCol]));
      return sortDir === 'asc' ? cmp : -cmp;
    })
  ), [filtered, listScope, sortCol, sortDir]);

  useEffect(() => { setPage(1); }, [filtered, listScope, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(tableRows.length / TABLE_PAGE_SIZE));
  const pagedRows = tableRows.slice((page - 1) * TABLE_PAGE_SIZE, page * TABLE_PAGE_SIZE);

  function toggleSort(col) {
    if (sortCol === col) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  }

  const selectedIds = useMemo(() => new Set(compareSelection.map(g => g.unique_game_id)), [compareSelection]);
  const selectionFull = compareSelection.length >= MAX_COMPARE_GAMES;

  function toggleCompare(g) {
    setCompareSelection(sel => {
      if (sel.some(s => s.unique_game_id === g.unique_game_id)) return sel.filter(s => s.unique_game_id !== g.unique_game_id);
      if (sel.length >= MAX_COMPARE_GAMES) return sel;
      return [...sel, toPerformanceGame(g)];
    });
  }

  // Game Performance opens on a range starting at the earliest release, so
  // every compared game's launch is in view.
  function openPerformance(games) {
    navigate('/game-department/performance', {
      state: { games, range: comparisonRange(games, todayUtc()) },
    });
  }

  const colCount = 8 + METRIC_COLS.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Game Releases</CardTitle>
        <CardDescription>
          Games by release date, with how they were launched — as a pre-release (live before the general release,
          usually first a VIP-only week and then a week open to everyone) or normally — and how they performed during
          their pre-release period or, to compare with normal releases, over their first days after going live. Games released before release tracking began use
          their first-seen date and show as untracked. Tick up to {MAX_COMPARE_GAMES} games to compare them
          in Game Performance.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && <Alert variant="error" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <label className="text-[11px] text-ink-dim mb-1 block">Released from</label>
            <Input
              type="date" value={range.from} max={range.to}
              onChange={e => e.target.value && setRange(r => ({ ...r, from: e.target.value }))}
              className="w-[150px]"
            />
          </div>
          <div>
            <label className="text-[11px] text-ink-dim mb-1 block">To</label>
            <Input
              type="date" value={range.to} min={range.from}
              onChange={e => e.target.value && setRange(r => ({ ...r, to: e.target.value }))}
              className="w-[150px]"
            />
          </div>
          <div className="flex items-center gap-1">
            {DATE_RANGE_PRESETS.map(p => (
              <Button key={p.days} variant="ghost" size="sm" onClick={() => setRange(rangeForLastDays(p.days))}>
                {p.label}
              </Button>
            ))}
          </div>
          <div>
            <label className="text-[11px] text-ink-dim mb-1 block">Release type</label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[230px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPE_FILTERS.map(f => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-ink-dim mb-1 block">Provider</label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All providers</SelectItem>
                {providers.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-ink-dim mb-1 block">Stats over</label>
            <Select value={windowKey} onValueChange={setWindowKey}>
              <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {WINDOWS.map(w => <SelectItem key={w.key} value={w.key}>{w.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {prePeriod && (
          <ToggleGroup value={phase} onChange={setPhase} className="mb-4">
            {PHASES.map(p => <ToggleItem key={p.key} value={p.key}>{p.label}</ToggleItem>)}
          </ToggleGroup>
        )}

        {loading ? (
          <div className="flex justify-center py-10"><Spinner size="sm" /></div>
        ) : (
          <>
            <ReleaseTypeSummary summary={summary} windowKey={windowKey} phase={activePhase} />

            <ToggleGroup value={listScope} onChange={setListScope} className="mb-3">
              {LIST_SCOPES.map(s => (
                <ToggleItem key={s.key} value={s.key}>{s.label} ({scopeCounts[s.key].toLocaleString('en-GB')})</ToggleItem>
              ))}
            </ToggleGroup>

            <div className="flex flex-wrap items-center gap-3 mb-2 min-h-[32px]">
              <span className="text-[11px] text-ink-dim">
                {compareSelection.length === 0
                  ? `Tick up to ${MAX_COMPARE_GAMES} games to compare them in Game Performance.`
                  : `${compareSelection.length}/${MAX_COMPARE_GAMES} selected: ${compareSelection.map(g => g.game_title).join(', ')}`}
              </span>
              {compareSelection.length > 0 && (
                <div className="flex items-center gap-2 ml-auto">
                  <Button variant="ghost" size="sm" onClick={() => setCompareSelection([])}>Clear</Button>
                  <Button size="sm" onClick={() => openPerformance(compareSelection)}>
                    Compare {compareSelection.length} in Game Performance
                  </Button>
                </div>
              )}
            </div>

            <div className="border border-white/10 rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"><span className="sr-only">Compare</span></TableHead>
                    <SortableHead col="game_title" label="Game" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                    <SortableHead col="provider" label="Provider" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                    <SortableHead col="release_type" label="Type" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                    <SortableHead col="release_date" label="Released" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                    <TableHead className="whitespace-nowrap">General release</TableHead>
                    <TableHead className="whitespace-nowrap">{prePeriod ? 'Pre-release period' : `First ${windowKey} days`}</TableHead>
                    <TableHead className="whitespace-nowrap">VIP-only window</TableHead>
                    {METRIC_COLS.map(m => (
                      <SortableHead key={m.key} col={m.key} label={m.label} sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} right />
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colCount} className="text-center text-ink-dim text-xs py-8">
                        No games released in this range match these filters
                      </TableCell>
                    </TableRow>
                  ) : pagedRows.map(g => (
                    <ReleaseRow
                      key={g.unique_game_id}
                      g={g}
                      prePeriod={prePeriod}
                      selected={selectedIds.has(g.unique_game_id)}
                      disabled={selectionFull && !selectedIds.has(g.unique_game_id)}
                      onToggle={toggleCompare}
                      onOpen={game => openPerformance([toPerformanceGame(game)])}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
            <TablePagination page={page} totalPages={totalPages} total={tableRows.length} onPageChange={setPage} />
          </>
        )}
      </CardContent>
    </Card>
  );
}
